import { cn } from "@/utils";
import { useLocalStorageState } from "ahooks";
import { ChevronRight } from "lucide-react";

/**
 * Section header used by the explorer scopes (Files, Nodes, ...).
 *
 * Two modes:
 *
 *  - **Static** (no `storageKey`): just renders the label + count.
 *    Used for one-off section headings that don't need to remember
 *    state across sessions.
 *  - **Collapsible** (with `storageKey`): becomes a clickable header
 *    that hides its sibling content and persists the state under the
 *    given key via `useLocalStorageState`. `defaultCollapsed` chooses
 *    the initial value the first time the user sees the header — pass
 *    `true` for low-priority sections (e.g. canvas Nodes) so the user
 *    isn't met with a wall of rows on first open.
 *
 * Renders the chevron on the *left* of the label so the affordance reads
 * as a real folder-style toggle (matches the existing CanvasExplorer
 * accordions and `WorkspaceSidebar`'s "Workspaces" toggle).
 */
export function SectionHeader({
  label,
  count,
  storageKey,
  defaultCollapsed = false,
  rightSlot,
  children,
}: {
  label: string;
  count?: number;
  /** Persistence key. Omit for a non-interactive header. */
  storageKey?: string;
  /** Initial state on first paint when no stored value exists. */
  defaultCollapsed?: boolean;
  /** Optional render slot to the right of the count (e.g. an action). */
  rightSlot?: React.ReactNode;
  /** Section body — shown when expanded. Required when `storageKey` is set. */
  children?: React.ReactNode;
}) {
  const [collapsed, setCollapsed] = useLocalStorageState<boolean>(
    storageKey ?? "__explorer_section_header_unused__",
    {
      defaultValue: defaultCollapsed,
    },
  );

  const isCollapsible = !!storageKey;
  const isCollapsed = isCollapsible ? !!collapsed : false;

  // Lifted from `WorkspaceSidebar`'s "Workspaces" toggle so the
  // mechanic + visual feel like the same family of controls — full-row
  // hover background, chevron-right that rotates 90° when expanded,
  // generous (h-9) target so the entire strip feels clickable.
  const header = (
    <div
      role={isCollapsible ? "button" : undefined}
      tabIndex={isCollapsible ? 0 : undefined}
      aria-expanded={isCollapsible ? !isCollapsed : undefined}
      onClick={isCollapsible ? () => setCollapsed((v) => !v) : undefined}
      onKeyDown={
        isCollapsible
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setCollapsed((v) => !v);
              }
            }
          : undefined
      }
      className={cn(
        "group flex items-center gap-2 h-9 mt-2 mx-2 px-2.5 rounded-lg",
        isCollapsible &&
          cn(
            "cursor-pointer select-none",
            "text-foreground/75 hover:text-foreground",
            "hover:bg-foreground/[0.05] dark:hover:bg-n-95/[0.05]",
          ),
      )}
    >
      <span className="flex-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/90 group-hover:text-foreground/80 text-left">
        {label}
      </span>
      {typeof count === "number" && (
        <span className="text-[11px] tabular-nums text-muted-foreground/60 group-hover:text-foreground/60">
          {count}
        </span>
      )}
      {rightSlot}
      {isCollapsible && (
        <ChevronRight
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-muted-foreground/40 group-hover:text-foreground/60 transition-all duration-150",
            !isCollapsed && "rotate-90",
          )}
        />
      )}
    </div>
  );

  if (!isCollapsible) {
    return (
      <>
        {header}
        {children}
      </>
    );
  }

  return (
    <>
      {header}
      {!isCollapsed && children}
    </>
  );
}

export function SectionEmptyState({ message }: { message: string }) {
  return (
    <div className="mx-4 my-2 rounded-lg border border-dashed bg-muted/20 px-3 py-4 text-center">
      <p className="text-xs text-muted-foreground">{message}</p>
    </div>
  );
}
